/**
 * Left navigation on the category and article pages: every category, expandable
 * down to its groups and articles. Built from the same rows as the settings
 * navigation, so the two read as one product.
 *
 * Nothing collapses on its own. Opening a category does not close the one you
 * were in, opening a group does not close its sibling, and following a link does
 * not reset any of it — the component stays mounted from article to article, so
 * the tree a reader opened is still open when the next page arrives. Groups start
 * expanded, so opening a category shows every page under it rather than one more
 * level to click through.
 */

import { buttonVariants } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowLeft, ChevronRight } from "lucide-react"
import { AnimatePresence, useReducedMotion } from "motion/react"
import * as m from "motion/react-m"
import { useEffect, useState } from "react"
import { Link } from "react-router"
import type { HelpContent } from "../api/types"
import { fold, TRAVEL } from "../motion"
import { HELP_BASE, helpArticlePath } from "../routes"
import { visualIcon } from "./CategoryVisual"

const ROW_CLASS = cn(
  buttonVariants({ variant: "ghost", size: "sm" }),
  "w-full justify-start font-normal",
)

interface HelpdeskArticleSidebarProps {
  content: HelpContent
  /** Absent on a category page, where no article is open. */
  activeArticleId?: string
  activeCategoryId: string
}

/** The set with `id` added or removed. */
function toggled(set: Set<string>, id: string): Set<string> {
  const next = new Set(set)
  if (!next.delete(id)) next.add(id)
  return next
}

export function HelpdeskArticleSidebar({
  content,
  activeArticleId,
  activeCategoryId,
}: HelpdeskArticleSidebarProps) {
  const { translate } = useTranslation()
  const foldMotion = fold(useReducedMotion())
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    () => new Set([activeCategoryId]),
  )
  // Groups are open unless someone closed them, which is what makes "expand a
  // category" show all of its pages at once.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(new Set())

  // Reading a page in another category opens that branch, and only adds.
  useEffect(() => {
    setOpenCategories((open) =>
      open.has(activeCategoryId) ? open : new Set(open).add(activeCategoryId),
    )
  }, [activeCategoryId])

  return (
    <m.aside
      layoutScroll
      className="hidden w-64 shrink-0 overflow-y-auto border-r p-4 @3xl:block"
    >
      <nav
        aria-label={translate("HELPDESK/TITLE")}
        className="flex flex-col gap-1"
      >
        <Link to={HELP_BASE} className={cn(ROW_CLASS, "mb-3")}>
          <ArrowLeft aria-hidden="true" />
          {translate("HELPDESK/TITLE")}
        </Link>

        {content.categories.map((category) => {
          const isOpen = openCategories.has(category.id)
          const Icon = visualIcon(category.visualKey)

          return (
            <div key={category.id} className="flex flex-col gap-0.5">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() =>
                  setOpenCategories((open) => toggled(open, category.id))
                }
                className={cn(
                  ROW_CLASS,
                  activeCategoryId === category.id && "font-medium",
                )}
              >
                <Icon aria-hidden="true" />
                {category.title}
                <ChevronRight
                  aria-hidden="true"
                  className={cn(
                    "ml-auto text-muted-foreground transition-transform duration-150 motion-reduce:transition-none",
                    isOpen && "rotate-90",
                  )}
                />
              </button>

              <AnimatePresence initial={false}>
                {isOpen ? (
                  <m.div
                    key="groups"
                    {...foldMotion}
                    className="flex flex-col gap-0.5"
                  >
                    {category.groups.map((group) => {
                      const isGroupOpen = !closedGroups.has(group.id)
                      return (
                        <div key={group.id} className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() =>
                              setClosedGroups((closed) =>
                                toggled(closed, group.id),
                              )
                            }
                            aria-expanded={isGroupOpen}
                            className="mt-1 flex w-full items-center rounded-md py-1 pr-3 pl-9 text-left text-xs text-muted-foreground transition-colors duration-150 hover:text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                          >
                            {group.title}
                            <ChevronRight
                              aria-hidden="true"
                              className={cn(
                                "ml-auto size-3 transition-transform duration-150 motion-reduce:transition-none",
                                isGroupOpen && "rotate-90",
                              )}
                            />
                          </button>

                          <AnimatePresence initial={false}>
                            {isGroupOpen ? (
                              <m.ul
                                key="articles"
                                {...foldMotion}
                                className="flex flex-col gap-0.5"
                              >
                                {group.articles.map((article) => {
                                  const isCurrent =
                                    article.id === activeArticleId
                                  return (
                                    <li key={article.id}>
                                      <Link
                                        to={helpArticlePath(
                                          category.id,
                                          article.id,
                                        )}
                                        aria-current={
                                          isCurrent ? "page" : undefined
                                        }
                                        className={cn(
                                          ROW_CLASS,
                                          "relative h-auto py-1.5 pr-3 pl-9 text-left leading-snug whitespace-normal",
                                          isCurrent &&
                                            "font-medium text-primary hover:bg-transparent hover:text-primary",
                                        )}
                                      >
                                        {/* The settings navigation's current page,
                                            travelling from the page before it. */}
                                        {isCurrent ? (
                                          <m.span
                                            layoutId="helpdesk-current-article"
                                            transition={TRAVEL}
                                            className="absolute inset-0 rounded-md bg-primary/10"
                                          />
                                        ) : null}
                                        <span className="relative">
                                          {article.title}
                                        </span>
                                      </Link>
                                    </li>
                                  )
                                })}
                              </m.ul>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      )
                    })}
                  </m.div>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </nav>
    </m.aside>
  )
}
